/**
 * Palette and panel copy for the sign-in screens, from the "Compliance Sign-in"
 * design canvas.
 *
 * WHY THIS IS A LOCAL PALETTE AND NOT NEW BRAND TOKENS. These colours are the
 * fixed product ground for the front door only — the warm paper, the
 * role-coloured panel, the field wells. They deliberately do NOT go through
 * `--brand-*` or `--ui-*`: those layers are tenant-overridable (branding.tsx
 * rewrites --brand-primary at runtime from the tenant's primaryColor) and are
 * read by every other screen in three apps. Adding a fourth palette to them to
 * serve two routes would make every consumer's rendering depend on a decision
 * that only sign-in cares about.
 *
 * What DOES stay tenant-driven on these screens, and must not be moved in here:
 * the lockup (`branding.logoUrl` before the product mark) and the primary
 * button's fill (`var(--brand-accent)`, via <Btn>). A tenant who has set a
 * purple brand still gets a purple "Email me a sign-in code".
 */

export type SignInAudience = 'admin' | 'company' | 'driver';

/**
 * The canvas's two families. Applied as inline `fontFamily` rather than a
 * Tailwind `font-*` alias because the three apps define DIFFERENT `fontFamily`
 * theme keys (admin ships Geist, portal and ops ship the system stack), so one
 * alias cannot mean the same thing in all of them — and an undefined Tailwind
 * utility emits nothing at all rather than failing the build.
 */
export const SIGNIN_SANS = "Archivo, 'Helvetica Neue', Helvetica, Arial, sans-serif";
export const SIGNIN_MONO =
  "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";

/** Shared page-side colours — identical for all three audiences. */
export const SIGNIN_COLORS = {
  /** The page ground. Warm paper, not slate. */
  paper: '#F7F6EF',
  ink: '#14120D',
  /** Body copy on paper. */
  body: '#5B5950',
  /** Field and control labels. */
  label: '#35342D',
  /** Mono eyebrows on the paper side. */
  eyebrow: '#6B7A6E',
  /** Hairline rule between the form and the help text. */
  rule: '#E2E0D5',
  /** Field well — very slightly warmer than white, so it reads as inset. */
  fieldBg: '#FFFDF7',
  /** Focus ring / filled-cell border. */
  focus: '#244232',
  /** The passwordless note. */
  noteBg: '#EFF2E9',
  noteBorder: '#DFE4D5',
  noteText: '#47513F',
  noteDot: '#6B8A73',
} as const;

/**
 * Per-audience panel colours.
 *
 * `border` and `fieldBorder` differ for the driver because the driver side of
 * the canvas sits on sand rather than paper and needs a warmer hairline to read
 * at all; admin and company share the neutral one.
 */
export const SIGNIN_THEME: Record<
  SignInAudience,
  {
    /** Panel ground. */
    panelBg: string;
    /** Panel foreground. */
    panelFg: string;
    /** Panel eyebrow + trailing meta. */
    panelMuted: string;
    /** Panel body copy. */
    panelBody: string;
    /** Panel footer line — quieter again than panelMuted. */
    panelFoot: string;
    /** Inverted chip behind the mark in the panel and mobile bar. */
    chipBg: string;
    /** Field border on this audience's form side. */
    fieldBorder: string;
  }
> = {
  admin: {
    panelBg: '#14120D',
    panelFg: '#F7F6EF',
    panelMuted: '#9C978A',
    panelBody: '#C9C5B8',
    panelFoot: '#77736A',
    chipBg: '#F7F6EF',
    fieldBorder: '#DDDACD',
  },
  company: {
    panelBg: '#1E3728',
    panelFg: '#F7F6EF',
    panelMuted: '#8FAE9A',
    panelBody: '#CBD8CE',
    panelFoot: '#6F8A78',
    chipBg: '#F7F6EF',
    fieldBorder: '#DDDACD',
  },
  driver: {
    // The driver panel is the one LIGHT panel: sand, dark ink on it.
    panelBg: '#EFE7D4',
    panelFg: '#14120D',
    panelMuted: '#7A6B4A',
    // Same as panelFg: on the light sand panel the canvas lets the step cards
    // inherit full-strength ink rather than dropping to a muted body colour,
    // which would lose contrast against #F7F2E5.
    panelBody: '#14120D',
    panelFoot: '#8A8270',
    chipBg: '#14120D',
    fieldBorder: '#DED3B9',
  },
};

/** Sand-side card colours — the driver panel's numbered step cards. */
export const DRIVER_STEP = {
  bg: '#F7F2E5',
  border: '#E2D7BE',
  num: '#6A5A32',
} as const;

/** The sand panel's edge against the paper form column. */
export const DRIVER_PANEL_EDGE = '#DED3B9';

/** A document row in the company panel. `warn` is the amber "due soon" state. */
export type PanelRow = { label: string; meta: string; tone: 'ok' | 'warn' };

export type SignInPanel = {
  /** Mono, uppercase, above the panel heading. */
  eyebrow: string;
  heading: string;
  /** Admin: dotted bullets. */
  bullets?: readonly string[];
  /** Company: document rows with expiry metadata. */
  rows?: readonly PanelRow[];
  /** Company: a closing sentence under the rows. */
  note?: string;
  /** Driver: numbered step cards. */
  steps?: readonly string[];
  /** Driver only — the one-line promise on the compact mobile bar. */
  mobileTagline?: string;
  /** Mono, uppercase, at the right of the compact mobile bar. */
  mobileBadge: string;
};

/**
 * Panel copy.
 *
 * EVERY CLAIM HERE WAS CHECKED AGAINST THE CODE, and three of the canvas's were
 * cut because the feature does not exist:
 *
 *  - "Certificates are read automatically — nothing to retype" and "Photograph
 *    your licence — it reads itself, no typing" both promise OCR field
 *    extraction. The verifier returns `{criterion, met, confidence, evidence}`
 *    (packages/shared/src/docVerification.ts) — it JUDGES admin-authored
 *    criteria against an upload. Nothing writes an expiry date back into a form.
 *  - "Show your QR pass at the gate": the only QR in the product is ops TOTP
 *    enrolment, plus a VISITOR landing URL that apps/portal/src/App.tsx notes
 *    "nothing in code emits". Drivers have no gate pass.
 *  - "For SupplierClear staff": the admin app serves the TENANT's compliance
 *    team. Platform staff use apps/ops.
 *
 * What survived is anchored in shipped code: auto-pass confidence thresholds
 * (apps/admin/src/admin/autoPassThresholdInput.ts, DocumentTypes.tsx), the AI
 * review screen (Verifications.tsx), the activity trail (api/src/routes/
 * activity.ts, admin/EntityHistory.tsx), expiry tracking (ExpiryChip,
 * api/src/lib/expiringDocuments.ts) and the driver's four real routes
 * (details / questionnaire / training / library, see portal/src/App.tsx).
 *
 * The company rows are ILLUSTRATIVE — sample document types, not a promise
 * about any particular tenant's register.
 */
export const SIGNIN_PANELS: Record<SignInAudience, SignInPanel> = {
  admin: {
    eyebrow: 'Internal · compliance operations',
    heading: 'Every supplier, document and expiry, on one register.',
    bullets: [
      'Review what the AI read on a document before you accept it.',
      'Set auto-pass confidence thresholds per document type and approve by exception.',
      'A full activity trail on every upload, review and status change.',
    ],
    mobileBadge: 'Admin',
  },
  company: {
    eyebrow: 'For suppliers & contractors',
    heading: 'Upload once. Stop chasing attachments.',
    rows: [
      { label: 'Public liability', meta: 'EXP 12 MAR 2027', tone: 'ok' },
      { label: 'Workers compensation', meta: 'DUE IN 9 DAYS', tone: 'warn' },
      { label: 'Goods in transit', meta: 'EXP 26 AUG 2026', tone: 'ok' },
    ],
    note: 'Sign in to see exactly what is outstanding and why — every document, its expiry, and what a reviewer asked for.',
    mobileBadge: 'Supplier portal',
  },
  driver: {
    eyebrow: 'For drivers & on-site workers',
    heading: 'Three things, then you are good to go.',
    steps: [
      'Add your details and upload your licence and certificates.',
      'Work through your questionnaire and your training.',
      'Keep it current — we email you before anything expires.',
    ],
    mobileTagline: 'Licences, inductions and checks — from your phone.',
    mobileBadge: 'Driver',
  },
};

/** Amber used for the company panel's "due soon" row. */
export const PANEL_ROW_WARN = '#E8C77A';
